import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import milestonesService, { type Milestone, type MilestoneInput } from '../../../services/projectManagement/milestonesService';
import { projectsService, type Project } from '../../../services/projectManagement/projectsService';

interface MilestonesPanelProps {
  organizationId: string;
}

const MilestonesPanel: React.FC<MilestonesPanelProps> = ({ organizationId }) => {
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('all');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [color, setColor] = useState('#3b82f6');
  const [projectId, setProjectId] = useState('');

  const projectNameById = useMemo(() => {
    const map = new Map<string, string>();
    projects.forEach(p => map.set(p.id, p.name));
    return map;
  }, [projects]);

  const fetchProjects = async () => {
    try {
      const { data, error } = await projectsService.list({ organization_id: organizationId, limit: 200 });
      if (error) return;
      setProjects(data);
      if (!projectId && data.length > 0) {
        setProjectId(data[0].id);
      }
    } catch {
      return;
    }
  };

  const fetchMilestones = async () => {
    setLoading(true);
    try {
      const { data, error } = await milestonesService.list({
        organization_id: organizationId,
        project_id: selectedProjectId !== 'all' ? selectedProjectId : undefined,
      });

      if (error) {
        toast.error('Failed to load milestones');
        setMilestones([]);
      } else {
        setMilestones(data || []);
      }
    } catch {
      toast.error('Failed to load milestones');
      setMilestones([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchProjects();
  }, [organizationId]);

  useEffect(() => {
    void fetchMilestones();
  }, [organizationId, selectedProjectId]);

  const toggleCompleted = async (m: Milestone) => {
    try {
      const { error } = await milestonesService.update(m.id, organizationId, { completed: !m.completed } as any);
      if (error) {
        toast.error('Failed to update milestone');
        return;
      }
      void fetchMilestones();
    } catch {
      toast.error('Failed to update milestone');
    }
  };

  const deleteMilestone = async (id: string) => {
    if (!window.confirm('Delete this milestone?')) return;

    try {
      const { error } = await milestonesService.delete(id, organizationId);
      if (error) {
        toast.error('Failed to delete milestone');
        return;
      }
      toast.success('Milestone deleted');
      void fetchMilestones();
    } catch {
      toast.error('Failed to delete milestone');
    }
  };

  const createMilestone = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectId) {
      toast.error('Select a project');
      return;
    }

    if (!name.trim() || !dueDate) {
      toast.error('Name and due date are required');
      return;
    }

    const payload: MilestoneInput = {
      organization_id: organizationId,
      project_id: projectId,
      name: name.trim(),
      description: description.trim() || undefined,
      due_date: dueDate,
      color,
    };

    try {
      const { error } = await milestonesService.create(payload);
      if (error) {
        toast.error(error);
        return;
      }

      toast.success('Milestone created');
      setShowCreateModal(false);
      setName('');
      setDescription('');
      setDueDate('');
      setColor('#3b82f6');
      void fetchMilestones();
    } catch {
      toast.error('Failed to create milestone');
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-gray-900">Milestones</div>
          <div className="text-xs text-gray-500">{milestones.length} total</div>
        </div>

        <button
          onClick={() => setShowCreateModal(true)}
          className="rounded-lg bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700 hover:bg-blue-100"
        >
          + Add
        </button>
      </div>

      <div className="mb-3">
        <select
          value={selectedProjectId}
          onChange={(e) => setSelectedProjectId(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="all">All Projects</option>
          {projects.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Loading milestones...</div>
      ) : milestones.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 p-6 text-center">
          <div className="text-2xl mb-1">🏁</div>
          <div className="text-sm text-gray-600">No milestones yet</div>
        </div>
      ) : (
        <div className="space-y-2">
          {milestones.map(m => (
            <div key={m.id} className="group flex items-start gap-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50">
              <button
                onClick={() => void toggleCompleted(m)}
                className="mt-0.5 h-5 w-5 rounded border border-gray-300 flex items-center justify-center"
                title={m.completed ? 'Mark incomplete' : 'Mark complete'}
              >
                {m.completed ? (
                  <span className="text-xs">✓</span>
                ) : null}
              </button>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full" style={{ backgroundColor: m.color || '#3b82f6' }} />
                  <div className={`text-sm font-medium truncate ${m.completed ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                    {m.name}
                  </div>
                </div>

                <div className="mt-1 text-xs text-gray-500">
                  Due: {new Date(m.due_date).toLocaleDateString()}
                  {m.project_id && projectNameById.get(m.project_id) ? ` • ${projectNameById.get(m.project_id)}` : ''}
                </div>

                {m.description ? (
                  <div className="mt-1 text-xs text-gray-600 line-clamp-2">{m.description}</div>
                ) : null}
              </div>

              <button
                onClick={() => void deleteMilestone(m.id)}
                className="opacity-0 group-hover:opacity-100 text-red-600 hover:text-red-800 transition-opacity"
                title="Delete"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {showCreateModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Create Milestone</h3>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>

            <form onSubmit={createMilestone} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Project</label>
                <select
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  required
                >
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Due date</label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Description (optional)</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Color</label>
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="mt-1 h-10 w-16 rounded border border-gray-300 p-1"
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default MilestonesPanel;
